import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid, GridExpandChangeEvent, GridNoRecords } from "@progress/kendo-react-grid";
import { ChooserColumn, EmptyState, Icon, Spinner, toast, useKendoGrid } from "@axum/ax-design";
import { updateOrderState } from "../../Dashboard/Tabs/retry/fetchers";
import { orderToOrderIdentifier } from "../../Dashboard/Tabs/retry/interfaces";
import OrderBubble, { OrderBubbleContext } from "./OrderBubble";
import { useOrderLogs } from "./OrderLogs";
import { useConfirm } from "./useConfirm";
import { OrderToolsProvider } from "./OrderTools";
import { LEAD_WIDTH, ORDER_COLUMNS, renderOrderColumns, toneRow, TOOLS_WIDTH, useOrderColumns } from "./orderColumns";
import { OrdersGridContainer } from "./styles";
import { useOrdersFailed } from "../../../utils/loadErrors";

const noop = () => undefined;

export interface GridEmpty {
  title: string;
  description: React.ReactNode;
  action?: React.ReactNode;
}

const NO_ORDERS: GridEmpty = { title: "No hay pedidos", description: "Probá con otro rango de fechas o con menos filtros." };

interface Props {
  tableData: any[];
  loading: boolean;
  requestNewPage: (skip?: number, take?: number, sort?: any) => void;
  take: number;
  skip: number;
  total: number;
  sort: any;
  setSort: (sort: any) => void;
  featuresAllowed: string[];
  empty?: GridEmpty;
}

const OrdersGrid = (props: Props) => {
  const empty = props.empty || NO_ORDERS;
  const host = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState<ChooserColumn[]>(ORDER_COLUMNS);
  const shown = useOrderColumns(host, columns, ORDER_COLUMNS, LEAD_WIDTH + TOOLS_WIDTH);
  useKendoGrid(host);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);
  const { showLogs, logsModal } = useOrderLogs();
  const { confirm, confirmDialog } = useConfirm();
  const failed = useOrdersFailed();

  useEffect(() => {
    setExpandedIds([]);
  }, [props.tableData]);

  const rows = useMemo(
    () => (props.tableData || []).map(order => ({ ...order, expanded: expandedIds.includes(order.id) })),
    [props.tableData, expandedIds]
  );
  const allExpanded = rows.length > 0 && rows.every(row => row.expanded);

  const activeSort = () => (Object.values(props.sort[0] || {}).filter(x => x !== null).length > 0 ? props.sort : null);

  const handleChangePage = (event: any) => {
    const { take, skip } = event.page;
    props.requestNewPage(skip, take, activeSort());
  };

  const handleChangeSort = (event: any) => {
    props.setSort(event.sort);
    props.requestNewPage(props.skip, props.take, event.sort);
  };

  const toggleRow = (event: GridExpandChangeEvent) => {
    const id = event.dataItem.id;
    setExpandedIds(ids => (event.value ? [...ids, id] : ids.filter(x => x !== id)));
  };

  const expandAll = (open: boolean) => setExpandedIds(open ? rows.map(row => row.id) : []);

  const refresh = () => props.requestNewPage(props.skip, props.take, activeSort());

  const updateState = (order: any, erpState: string) =>
    updateOrderState(erpState, [orderToOrderIdentifier(order)]).then(x => {
      if (x.notUpdated > 0) toast.show(x.error, { type: "danger" });
      else toast.show("Actualizado exitosamente", { type: "success" });
      props.requestNewPage();
    });

  return (
    <OrderBubbleContext.Provider value={{ featuresAllowed: props.featuresAllowed, showLogs, updateState, confirm }}>
      <OrderToolsProvider
        onRefresh={refresh}
        loading={props.loading}
        expanded={allExpanded}
        onExpandAll={expandAll}
        columns={columns}
        onColumnsChange={setColumns}
        onColumnsRestore={() => setColumns(ORDER_COLUMNS)}
      >
        <OrdersGridContainer ref={host}>
          <Grid
            className={`ax-kendo${props.loading ? " is-loading" : ""}`}
            data={rows}
            detail={OrderBubble}
            expandField="expanded"
            onExpandChange={toggleRow}
            rowRender={toneRow}
            scrollable="none"
            pageable
            sortable
            sort={props.sort}
            take={props.take}
            skip={props.skip}
            total={props.total}
            onPageChange={handleChangePage}
            onSortChange={handleChangeSort}
          >
            <GridNoRecords>
              {failed && !props.loading ? (
                <EmptyState
                  icon={<Icon name="warning" />}
                  title="No se pudieron traer los pedidos"
                  description="Volvé a aplicar los filtros para reintentar."
                />
              ) : (
                <EmptyState
                  icon={props.loading ? <Spinner /> : <Icon name="inbox" />}
                  title={props.loading ? "Buscando pedidos…" : empty.title}
                  description={props.loading ? undefined : empty.description}
                  action={props.loading ? undefined : empty.action}
                />
              )}
            </GridNoRecords>
            {renderOrderColumns(shown)}
          </Grid>
        </OrdersGridContainer>
      </OrderToolsProvider>
      {logsModal}
      {confirmDialog}
    </OrderBubbleContext.Provider>
  );
};

export const OrdersGridPlaceholder = () => (
  <OrdersGrid tableData={[]} loading requestNewPage={noop} take={20} skip={0} total={0} sort={[]} setSort={noop} featuresAllowed={[]} />
);

export default OrdersGrid;
